import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight, TwoCol } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, Mistake, DidYouKnow, TryThis, KeyIdea, Pull, Stat, StatRow,
  Steps, Step, Vocab, Term, NextUp, Compare, KeyHighlights, Highlight,
} from "@/components/Callouts";

// Body groove first — kick on 1 & 3 (chest thump), snare on 2 & 4 (clap),
// hi-hat on every "and". Then we translate the same pattern to piano.
const bodyGroove: RhythmCell[] = [
  { hand: "L", on: true,  pitches: ["C2"], caption: "Kick" },
  { hand: "B", on: true,  pitches: ["F#5"], caption: "hat" },
  { hand: "R", on: true,  pitches: ["E3"], caption: "Snap" },
  { hand: "B", on: true,  pitches: ["F#5"], caption: "hat" },
  { hand: "L", on: true,  pitches: ["C2"], caption: "Kick" },
  { hand: "B", on: true,  pitches: ["F#5"], caption: "hat" },
  { hand: "R", on: true,  pitches: ["E3"], caption: "Snap" },
  { hand: "B", on: true,  pitches: ["F#5"], caption: "hat" },
];

// Same groove, translated to piano: LH bass = "kick", RH chord = "snare",
// RH high-stab = "hat".
const pianoGroove: RhythmCell[] = [
  { hand: "L", on: true,  pitches: ["C3"], caption: "L bass" },
  { hand: "R", on: true,  pitches: ["G5"], caption: "stab" },
  { hand: "R", on: true,  pitches: ["C5", "E5", "G5"], caption: "chord" },
  { hand: "R", on: true,  pitches: ["G5"], caption: "stab" },
  { hand: "L", on: true,  pitches: ["G2"], caption: "L bass" },
  { hand: "R", on: true,  pitches: ["G5"], caption: "stab" },
  { hand: "R", on: true,  pitches: ["C5", "E5", "G5"], caption: "chord" },
  { hand: "R", on: true,  pitches: ["G5"], caption: "stab" },
];

export default function PianoFoundation17() {
  const { lesson, course, accent } = findLesson("piano-foundation", "17");
  const [view, setView] = useState<"body" | "piano">("body");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF17-Percussive-Grooving-Animated.mp4"
        caption={`Kick + snare on a 16-step drum grid → LH bass + RH chord on piano · same skeleton`}
        hint={`≈ 22 sec · drummer's logic, pianist's hands`}
      />
      <KeyHighlights>
        <Highlight>Every groove you can <strong>tap on your body</strong> can be translated note-for-note to the piano — kick becomes LH bass, snare becomes RH chord, hi-hat becomes a high stab.</Highlight>
        <Highlight>Drummers think in <strong>roles</strong>, not pitches: low/loud, mid/sharp, and high/sustained. Pianists who borrow that mindset suddenly groove like a band.</Highlight>
        <Highlight>The <strong>guitarist's strum-up/strum-down</strong> maps directly onto your two hands: down-strum = bass on the beat, up-strum = chord on the "and".</Highlight>
        <Highlight>You don't need a metronome to start — you need a <strong>chest, a thigh and a clap</strong>. The metronome comes after the body owns the feel.</Highlight>
        <Highlight>By the end of this lesson you'll have <strong>one transferable groove</strong> that works under any I-IV-V chord change in any tempo.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Where great grooves come from" title="Your body already knows the pattern" accent={accent.hex}>
        <p>
          Long before you sat at a piano, you were already grooving. You tapped along with songs in
          the car. You bobbed your head at a concert. You clapped on the chorus. That instinctive
          internal pulse — the one your body falls into without being asked — is the same pulse a
          drummer uses to drive a band. The trick of this lesson is to <strong>not invent a new
          groove for the piano</strong>, but to <em>translate</em> a groove your body already owns.
        </p>

        <KeyIdea>
          The piano is a <strong>rhythm instrument first</strong> and a melodic instrument second.
          The 88 keys are basically 88 tuned drums. If you can play a groove on a bongo, you can
          play it on a piano — the choreography is identical, only the surface changes.
        </KeyIdea>

        <p>
          Most beginners get stuck because they sit down and try to make the piano sound{" "}
          <em>pianistic</em> — flowing, melodic, harmonious. That's the wrong target for someone
          learning to accompany. The right target is <strong>danceable</strong>. A piano that makes
          your friend nod their head is doing more musical work than a piano playing a perfect
          scale.
        </p>
      </Insight>

      <Insight eyebrow="The translation map" title="Body parts → piano voices" accent={accent.hex}>
        <p>
          When a drummer thinks about a groove, they're juggling three roles: a <strong>low
          anchor</strong> (kick drum), a <strong>mid backbeat</strong> (snare), and a <strong>high
          texture</strong> (hi-hat or shaker). On the body, those roles become a chest thump, a
          clap, and a finger-snap. On the piano, they become left-hand bass, right-hand chord, and
          right-hand top-note stab. Same three roles, three different instruments.
        </p>

        <Vocab>
          <Term name="Groove" def="a repeating rhythmic feel that makes a listener move involuntarily." />
          <Term name="Backbeat" def="the snare-style accent on beats 2 and 4 in 4/4 — the heart of pop, rock and gospel." />
          <Term name="Pocket" def="the slightly-relaxed-back-of-the-beat zone where great grooves live." />
          <Term name="Comping" def="short for 'accompanying' — the chord/rhythm role under a soloist or singer." />
        </Vocab>

        <Compare
          leftLabel="Body groove · what a drummer feels"
          rightLabel="Piano groove · the translation"
          left={
            <ul className="space-y-1">
              <li><strong>Chest thump</strong> on beats 1 and 3 → low/loud</li>
              <li><strong>Hand clap</strong> on beats 2 and 4 → mid/sharp</li>
              <li><strong>Finger snap</strong> on every "and" → high/short</li>
              <li>Played on the body — <em>no instrument needed</em></li>
              <li>Tempo decided by your <em>walking pace</em></li>
            </ul>
          }
          right={
            <ul className="space-y-1">
              <li><strong>LH bass</strong> on 1 and 3 (root, then fifth-below)</li>
              <li><strong>RH chord</strong> stab on 2 and 4 (C-E-G triad)</li>
              <li><strong>RH high note</strong> on every "and" (a high G ping)</li>
              <li>Same roles, just delivered through 88 keys</li>
              <li>Now you can sing on top — your hands are doing what your body just did</li>
            </ul>
          }
        />
      </Insight>

      <Insight eyebrow="Toggle the two grooves" title="Hear the body version, then the piano version" accent={accent.hex}>
        <div className="mb-3 flex items-center gap-2">
          <button
            onClick={() => setView("body")}
            className={view === "body" ? "nsm-btn-primary" : "nsm-btn-ghost"}
          >
            Body groove
          </button>
          <button
            onClick={() => setView("piano")}
            className={view === "piano" ? "nsm-btn-primary" : "nsm-btn-ghost"}
          >
            Piano translation
          </button>
        </div>
        {view === "body" ? (
          <RhythmGrid
            cells={bodyGroove}
            subdivisions={2}
            beats={4}
            bpm={92}
            label="Kick (chest) · Snap (clap) · Hat (snap) — one bar of 4/4 in eighths"
          />
        ) : (
          <RhythmGrid
            cells={pianoGroove}
            subdivisions={2}
            beats={4}
            bpm={92}
            label="Same groove on piano · LH bass + RH chord + RH stab"
          />
        )}
        <p className="prose-nsm text-sm">
          Hit Play on each. The skeleton is identical: a low pulse on 1 and 3, a sharper hit on 2
          and 4, and a continuous high pattern filling the gaps. The piano version is just that
          skeleton wearing different clothes.
        </p>
      </Insight>

      <Insight eyebrow="Borrowing from other instruments" title="What guitar and drums can teach the piano" accent={accent.hex}>
        <p>
          Pianists tend to learn from pianists. That's a mistake. Some of the most useful
          rhythmic ideas on the piano come from <strong>instruments that don't even have keys</strong>.
          Here are three instruments worth stealing from on a regular basis:
        </p>

        <Steps title="Three instruments to borrow grooves from">
          <Step>
            <strong>Acoustic guitar — the strum pattern.</strong> Down-up-down-up is the most
            common pop strum. On piano: LH plays the down-strum (root note), RH plays the up-strum
            (chord). The down-strum is heavier, the up-strum is lighter. That down-up-down-up feel
            is half of folk and worship piano.
          </Step>
          <Step>
            <strong>Drum kit — kick + snare + hat.</strong> The three-role split we just covered.
            Once you internalise it, you'll hear it inside every Beatles, Bruno Mars and Coldplay
            song you ever played. The piano simply takes on all three roles at once.
          </Step>
          <Step>
            <strong>Bongo / cajón — the open-tone vs slap.</strong> A bongo player uses two pitches
            (low open tone, high slap) and gets infinite variety from rhythm alone. Pianists can do
            this with just a root note and a chord — minimal harmonic information, maximum
            rhythmic interest.
          </Step>
        </Steps>

        <DidYouKnow>
          When drummers describe what they're doing, they almost never name pitches — they name{" "}
          <em>roles</em> and <em>shapes</em>: "the kick is on the one", "the snare goes on the
          backbeat", "the hat is doing eighths". They think in <strong>function</strong>, not
          notes. A pianist who learns to think this way unlocks a different musical language: you
          stop asking "what notes do I play?" and start asking "what role am I filling?"
        </DidYouKnow>
      </Insight>

      <Insight eyebrow="Following your bandmates" title="Listen for the role each musician is filling" accent={accent.hex}>
        <p>
          When you sit in with a band, the drummer is already laying down kick + snare + hat. The
          bassist is doubling the kick on the low end. The guitarist might be strumming chords on
          the upbeats. <em>If you also play kick + snare + hat on the piano, you've doubled
          everyone</em> — and the band sounds cluttered. Your job is to <strong>find the gap and
          fill it</strong>.
        </p>

        <Pull>
          You want to follow the guitarist and do some justice to what the guitar rhythm
          is doing.
        </Pull>

        <Tip>
          A simple field rule: if the drummer is busy, you go sparse. If the drummer is sparse,
          you go busy. The total information in the band stays roughly constant, but it shifts
          between players bar by bar. That conversation is what listeners experience as "the
          band sounds tight."
        </Tip>
      </Insight>

      <Insight eyebrow="Practice prescription" title="Five-step daily ritual" accent={accent.hex}>
        <Steps title="Body → piano in five days">
          <Step>
            <strong>Day 1 · The body alone.</strong> No piano. Stand up, walk to a steady tempo,
            and add chest-thump on 1+3 and a clap on 2+4. Do this for a full minute. The body
            should feel the four-on-the-floor pulse without you counting.
          </Step>
          <Step>
            <strong>Day 2 · Add a finger-snap on every "and".</strong> Now you're juggling three
            roles — chest, clap, snap — at once. This is harder than it sounds; if you trip up,
            slow the walk to half tempo.
          </Step>
          <Step>
            <strong>Day 3 · Sit at the piano and translate.</strong> LH thumps a low C on 1+3.
            RH stabs a C-E-G triad on 2+4. Don't add the hi-hat yet — just the kick and snare.
            Repeat 30 times.
          </Step>
          <Step>
            <strong>Day 4 · Add the hat.</strong> RH plays a single high G between every chord
            (the "and" of each beat). Now you have all three roles. Slow it down — the hat is
            the hardest layer to keep even.
          </Step>
          <Step>
            <strong>Day 5 · Move chord changes through it.</strong> Same groove, but every two
            bars move from C to F to G to C. Three new bass notes to find while keeping the
            three-role groove running.
          </Step>
        </Steps>

        <TryThis duration="3 min" title="The body-to-piano test">
          Stand up, do the body groove for 30 seconds (chest + clap + snap). Walk straight to the
          piano without losing the feel and play the same groove with both hands. The transition
          is the test — if you have to stop and reset to start the piano version, the groove
          isn't yet in your body, only in your head.
        </TryThis>

        <Mistake>
          The most common mistake is <strong>letting the chord drown out the bass</strong>.
          Beginners hit the right-hand chord too hard because it has more notes in it. Fix: play
          the LH bass at full volume and the RH chord at <em>half volume</em>. The kick should
          always be the loudest hit in the groove.
        </Mistake>
      </Insight>

      <Insight eyebrow="The numbers" title="Why this lesson matters more than scales" accent={accent.hex}>
        <StatRow>
          <Stat value="3" label="Roles in any groove" color="amber" />
          <Stat value="2" label="Hands you have" color="blue" />
          <Stat value="80%" label="Of pop drum patterns covered" color="emerald" />
          <Stat value="0" label="Pitches required to start" color="violet" />
        </StatRow>
        <p className="prose-nsm text-sm">
          A pianist who can groove with just a root note and a chord plays better in a band than
          a pianist who knows every scale but can't keep time. Time is the foundation;
          everything else decorates it.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-18"
        label="Lesson 18 · Apply Chord Theory with Inversions"
        why="Now that you can groove rhythmically, the next step is to make your chord changes smoother — inversions let you move between chords without your hand jumping all over the keyboard."
      />
    </LessonShell>
  );
}
